import posthog from 'posthog-js';
import { logger } from '@/lib/logger';

// PostHog configuration from environment variables
// iii Agent Hub webhook: PostHog → iii-agent-hub.scott-4bd.workers.dev/webhooks/posthog
// Configure in PostHog dashboard: Data Pipelines → Destinations → Webhook
// Destination URL: https://iii-agent-hub.scott-4bd.workers.dev/webhooks/posthog
// Events to forward: signup_completed, subscription_started, subscription_cancelled, trial_started
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const isDev = import.meta.env.DEV;

let initialized = false;

export const initPostHog = () => {
  if (typeof window === 'undefined') return;
  if (initialized) return;

  if (!POSTHOG_KEY) {
    if (isDev) {
      logger.log('[PostHog] No API key found, analytics disabled');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // Identity - only track identified users
    person_profiles: 'identified_only',

    // Automatic tracking
    capture_pageview: false, // We'll handle this manually
    capture_pageleave: true,
    autocapture: true,

    // Exception autocapture
    capture_exceptions: true,

    // Session recording is OFF everywhere (2026-10-01): replays could capture rendered chat and strategy text,
    // and the Privacy Policy now states that session replay is not used. `isDev` no longer gates it.
    disable_session_recording: true,
    session_recording: {
      // Divorce site with financial/PII inputs — never record input contents in replays.
      maskAllInputs: true,
      maskTextSelector: '[data-mask]',
    },

    // Performance
    capture_performance: true,

    // Heatmaps (required by iii Agent Hub analytics integration)
    enable_heatmaps: true,

    // Privacy
    respect_dnt: false,
    opt_out_capturing_by_default: false,

    // Feature flags
    advanced_disable_decide: false,

    // Loaded callback
    loaded: (ph) => {
      if (isDev) {
        ph.debug();
        logger.log('[PostHog] Initialized with enhanced tracking');
      }
    },
  });

  // iii Agent Hub: stamp every event with the brand + first-touch acquisition
  // context so the hub can attribute SettleWise's conversions and behavior.
  try {
    const params = new URLSearchParams(window.location.search);
    const superProps: Record<string, string> = {
      brand: 'settlewise',
      referrer: document.referrer || 'direct',
      landing_page: window.location.pathname,
    };
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = params.get(k);
      if (v) superProps[k] = v;
    }
    posthog.register(superProps);
  } catch {
    // non-fatal: super-property registration must never block init
  }

  initialized = true;
};

// Check if PostHog is available
const isPostHogAvailable = () => Boolean(POSTHOG_KEY) && typeof window !== 'undefined';

// Identify user after login
export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    posthog.identify(userId, properties);
  }
};

// Reset user on logout
export const resetUser = () => {
  if (isPostHogAvailable()) {
    posthog.reset();
  }
};

// Track custom events with automatic context
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    posthog.capture(eventName, properties);
  } else if (isDev) {
    logger.log('[PostHog] Event:', eventName, properties);
  }
};

// Capture exceptions for PostHog Error Tracking dashboard
export const captureException = (error: Error, additionalProperties?: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    posthog.captureException(error, additionalProperties);
  } else if (isDev) {
    logger.error('[PostHog] Exception:', error, additionalProperties);
  }
};

// Set user properties (wrapped to suppress PostHog reloadFeatureFlags errors)
export const setUserProperties = (properties: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    try {
      posthog.people.set(properties);
    } catch (e) {
      if (isDev) logger.warn('[PostHog] setUserProperties error:', e);
    }
  }
};

// Set user properties once (won't overwrite)
export const setUserPropertiesOnce = (properties: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    try {
      posthog.people.set_once(properties);
    } catch (e) {
      if (isDev) logger.warn('[PostHog] setUserPropertiesOnce error:', e);
    }
  }
};

// Increment a numeric user property
export const incrementUserProperty = (property: string, value: number = 1) => {
  if (isPostHogAvailable()) {
    posthog.people.set({ [property]: value });
  }
};

// Group users (e.g., by subscription tier)
export const setGroup = (groupType: string, groupKey: string, properties?: Record<string, unknown>) => {
  if (isPostHogAvailable()) {
    posthog.group(groupType, groupKey, properties);
  }
};

// Feature flags
export const isFeatureEnabled = (flagKey: string): boolean => {
  if (isPostHogAvailable()) {
    return posthog.isFeatureEnabled(flagKey) ?? false;
  }
  return false;
};

export const getFeatureFlag = (flagKey: string): string | boolean | undefined => {
  if (isPostHogAvailable()) {
    return posthog.getFeatureFlag(flagKey);
  }
  return undefined;
};

// Get person property (useful for geo data like $geoip_subdivision_1_code)
export const getPersonProperty = (key: string): string | undefined => {
  if (isPostHogAvailable()) {
    const props = posthog.get_property(key);
    return typeof props === 'string' ? props : undefined;
  }
  return undefined;
};

// Alias anonymous user to identified user
export const aliasUser = (alias: string) => {
  if (isPostHogAvailable()) {
    posthog.alias(alias);
  }
};

// Pre-defined event tracking functions for backwards compatibility
export const analytics = {
  trackEvent,

  // Dashboard events
  dashboardViewed: (section: string = 'overview') => {
    trackEvent('dashboard_viewed', { section });
  },

  // Strategy events
  strategyGenerated: () => {
    trackEvent('strategy_generated');
  },

  strategyRegenerated: () => {
    trackEvent('strategy_regenerated');
  },

  // Advisor-sponsored usage tracking (for margin analysis)
  advisorSeatUsage: {
    strategyGenerated: (seatAssignmentId: string) => {
      trackEvent('advisor_seat_strategy_generated', {
        seat_assignment_id: seatAssignmentId,
        usage_type: 'strategy',
      });
    },
    documentUploaded: (seatAssignmentId: string, documentCount: number = 1) => {
      trackEvent('advisor_seat_document_uploaded', {
        seat_assignment_id: seatAssignmentId,
        usage_type: 'document',
        document_count: documentCount,
      });
    },
    tradeProposalGenerated: (seatAssignmentId: string) => {
      trackEvent('advisor_seat_trade_proposal_generated', {
        seat_assignment_id: seatAssignmentId,
        usage_type: 'trade_proposal',
      });
    },
  },

  scenarioViewed: (scenarioId: string, scenarioName: string) => {
    trackEvent('scenario_viewed', { scenario_id: scenarioId, scenario_name: scenarioName });
  },

  scenarioTargetSet: (scenarioId: string, scenarioName: string) => {
    trackEvent('scenario_target_set', { scenario_id: scenarioId, scenario_name: scenarioName });
  },

  compareModeToogled: (enabled: boolean) => {
    trackEvent('compare_mode_toggled', { enabled });
  },

  // Script events
  scriptCopied: (scriptType: string) => {
    trackEvent('script_copied', { script_type: scriptType });
  },

  // Notes events
  noteCreated: (noteType: string) => {
    trackEvent('note_created', { note_type: noteType });
  },

  noteDeleted: () => {
    trackEvent('note_deleted');
  },

  // Intake flow events
  intakeStarted: () => {
    trackEvent('intake_started');
  },

  intakeCompleted: () => {
    trackEvent('intake_completed');
  },

  // Goals events
  goalsCompleted: () => {
    trackEvent('goals_completed');
  },

  // Onboarding events
  onboardingCompleted: () => {
    trackEvent('onboarding_completed');
  },
};

export default posthog;
